import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import { eur } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import Sezione from "@/components/Sezione";
import AzioniCliente from "@/components/AzioniCliente";
import ClienteAnagrafica from "@/components/ClienteAnagrafica";
import ClienteContabilita from "@/components/ClienteContabilita";
import ClienteDocumenti from "@/components/ClienteDocumenti";
import ComunicazioniBox from "@/components/ComunicazioniBox";
import BloccoAltro from "@/components/BloccoAltro";
import ReferenteSelect from "@/components/ReferenteSelect";
import AnagraficaDialog from "@/components/AnagraficaDialog";
import UploadDialog from "@/components/UploadDialog";
import DocumentoDialog from "@/components/GeneraDocumentoDialog";
import NuovaPraticaDialog from "@/components/NuovaPraticaDialog";
import ConfirmDialog from "@/components/ConfirmDialog";

const nomeRif = (r) => (r ? [r.cognome, r.nome].filter(Boolean).join(" ") : "RB Studio");
const titoloRiga = (p) => (p.titolo && !p.titolo.includes(p.nome) ? `${p.nome} · ${p.titolo}` : p.nome);
const dataIt = (v) => (/^\d{4}-\d{2}-\d{2}/.test(v || "") ? `${v.slice(8, 10)}/${v.slice(5, 7)}/${v.slice(0, 4)}` : (v || ""));

function RigaPratica({ p }) {
  return (
    <Link to={`/pratiche/${p.id}`} data-testid={`cliente-pratica-${p.id}`}
      className="riga gap-4 px-3.5 sm:px-5 py-3.5 no-underline text-[#1A1A1A] hover:bg-[#FAFAF7] transition-colors flex-wrap">
      <span className="flex-[999_1_240px] min-w-0 flex flex-col gap-0.5">
        <span className="text-[16px] font-bold text-[#1B2A4B]">{titoloRiga(p)}</span>
        <span className="text-[13px] text-[#5A5A5A]">
          aperta il {p.aperta_il}{p.step_corrente ? ` · in corso: ${p.step_corrente}` : " · tutti gli step completati"}
        </span>
      </span>
      <span className="w-[130px] shrink-0 flex flex-col gap-[3px]">
        <span className="block h-[7px] bg-[#E8E6DF] rounded-[4px] overflow-hidden">
          <span className="block h-[7px] bg-[#A8863F]" style={{ width: `${p.perc}%` }} />
        </span>
        <span className="text-[12px] text-[#5A5A5A]">{p.fatti} di {p.totale_step} step</span>
      </span>
      <span className="w-[150px] shrink-0 sm:text-right">
        <span className="text-[16px] font-bold">{eur(p.valore)}</span>
        <span className="block text-[12px] text-[#5A5A5A]">
          {p.valore <= 0 ? "valore da concordare"
            : p.da_incassare <= 0 ? "tutto incassato"
            : `${eur(p.da_incassare)} da incassare`}
        </span>
      </span>
    </Link>
  );
}

export default function ClienteDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [tutti, setTutti] = useState(false);
  const [anaOpen, setAnaOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [praticaOpen, setPraticaOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editRif, setEditRif] = useState(false);
  const [azione, setAzione] = useState(null);

  const { data: s, isLoading } = useQuery({
    queryKey: ["cliente-scheda", id],
    queryFn: async () => (await api.get(`/clienti/${id}/scheda`)).data,
  });
  const { data: drive, isLoading: driveLoading, refetch: refetchFiles } = useQuery({
    queryKey: ["drive-files", id],
    queryFn: async () => (await api.get(`/clienti/${id}/drive-files`)).data,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["cliente-scheda", id] });
    qc.invalidateQueries({ queryKey: ["clienti"] });
    qc.invalidateQueries({ queryKey: ["pratiche"] });
  };

  if (isLoading || !s) {
    return <div className="flex items-center gap-2 text-sm text-[#4a5568]"><Loader2 className="w-4 h-4 animate-spin" /> Carico la scheda…</div>;
  }

  const c = s.cliente;
  const aperte = s.pratiche.filter((p) => !p.chiusa);
  const chiuse = s.pratiche.filter((p) => p.chiusa);
  const vuotiNascosti = s.anagrafica.reduce(
    (n, g) => n + g.campi.filter((x) => !x.valore && x.richiesto_da.length === 0).length, 0);
  const sottotitolo = [c.nazionalita, c.data_nascita ? `nato il ${dataIt(c.data_nascita)}` : "",
    [c.residenza_citta, c.residenza_provincia ? `(${c.residenza_provincia})` : ""].filter(Boolean).join(" ")]
    .filter(Boolean).join(" · ");

  const salvaRif = async (rid) => {
    try {
      await api.patch(`/clienti/${id}/anagrafica`, { referente_id: rid || null });
      toast.success("Segnalatore aggiornato"); setEditRif(false); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Aggiornamento non riuscito"); }
  };

  const vaiAzione = (tipo) => {
    if (s.pratiche.length === 0) return toast.error("Il denaro vive nella pratica: crea prima una pratica.");
    const lista = aperte.length ? aperte : s.pratiche;
    if (lista.length === 1) return navigate(`/pratiche/${lista[0].id}?azione=${tipo}`);
    setAzione(tipo);
  };

  const chiediElimina = () => {
    if (s.pratiche.length > 0) {
      return toast.error("Ha pratiche collegate: si può eliminare solo un cliente senza pratiche.");
    }
    setDeleteOpen(true);
  };

  const elimina = async () => {
    try {
      await api.delete(`/clienti/${id}`);
      toast.success("Cliente eliminato");
      qc.invalidateQueries({ queryKey: ["clienti"] });
      navigate("/clienti");
    } catch (e) { toast.error(e?.response?.data?.detail || "Eliminazione non riuscita"); }
    finally { setDeleteOpen(false); }
  };

  return (
    <div data-testid="cliente-detail-page"
      className="scheda-rb -m-4 sm:-m-5 lg:-m-7 p-4 sm:p-5 lg:p-7 bg-[#F2F1EC] min-h-screen">
      <div className="w-full max-w-[1120px] mx-auto flex flex-col gap-[18px]">
        <Link to="/clienti" className="text-[14px] font-semibold no-underline">← Clienti</Link>

        {/* 0 · Intestazione */}
        <header className="flex flex-col gap-3">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex flex-col gap-0.5">
              <h1 data-testid="cliente-nome" className="text-[25px] md:text-[30px] font-bold leading-[1.15] text-[#1B2A4B] break-words">
                {c.nome} {c.cognome}
              </h1>
              {sottotitolo && <p data-testid="cliente-sottotitolo" className="text-[14px] md:text-[16px] text-[#4A4A48]">{sottotitolo}</p>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span data-testid="cliente-rif" className="text-[13px] text-[#4A4A48] bg-[#ECEAE3] rounded-[3px] px-3 py-1.5">
                Rif. {nomeRif(c.referente)}
              </span>
              {aperte.length > 0 && (
                <span data-testid="cliente-pratiche-badge" className="text-[13px] font-bold text-[#1B3A22] bg-[#D8E6D2] rounded-[3px] px-3 py-1.5">
                  {aperte.length} pratic{aperte.length === 1 ? "a" : "he"} in corso
                </span>
              )}
              <AzioniCliente cliente={c} nPratiche={s.pratiche.length} puoEliminare={can("elimina_clienti")}
                onAnagrafica={() => setAnaOpen(true)} onSegnalatore={() => setEditRif(true)}
                onCarica={() => setUploadOpen(true)} onElimina={chiediElimina} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <button data-testid="cliente-crea-pratica" onClick={() => setPraticaOpen(true)} className="btn btn-g btn-larghezza">Crea pratica</button>
            <button data-testid="cliente-genera-doc" onClick={() => setGenOpen(true)} className="btn btn-o btn-larghezza">Genera documento</button>
            <button data-testid="cliente-carica-doc" onClick={() => setUploadOpen(true)} className="btn btn-o btn-larghezza">Carica documenti</button>
            <button data-testid="cliente-modifica-anagrafica" onClick={() => setAnaOpen(true)} className="btn btn-o btn-larghezza">Modifica anagrafica</button>
          </div>
          {editRif && (
            <div className="sez p-4 max-w-md">
              <ReferenteSelect value={c.referente_id || ""} onChange={salvaRif} testid="cliente-rif-select" />
              <button data-testid="cliente-rif-close" onClick={() => setEditRif(false)} className="text-[13px] mt-2 font-semibold">Chiudi</button>
            </div>
          )}
        </header>

        {/* 1 · Pratiche */}
        <Sezione testid="sez-cliente-pratiche" titolo="Pratiche" piatto
          azioni={<span className="text-[13px] text-[#E7D9B2]">{aperte.length} in corso · {chiuse.length} chiuse</span>}>
          <div className="flex flex-col">
            {s.pratiche.length === 0 && (
              <div className="riga px-3.5 sm:px-5 py-4 text-[#5A5A5A]" data-testid="cliente-pratiche-vuoto">
                Nessuna pratica ancora aperta. Usa «Crea pratica» qui sopra.
              </div>
            )}
            {aperte.map((p) => <RigaPratica key={p.id} p={p} />)}
            {chiuse.length > 0 && (
              <>
                <div className="px-3.5 sm:px-5 py-2.5 bg-[#F7F6F2] border-t border-[#E8E6DF]">
                  <span className="eti">Pratiche chiuse · {chiuse.length}</span>
                </div>
                {chiuse.map((p) => <RigaPratica key={p.id} p={p} />)}
              </>
            )}
            {s.pratiche.length > 0 && chiuse.length === 0 && (
              <div className="nota-tenue">Le pratiche chiuse restano qui, in fondo, raggruppate.</div>
            )}
          </div>
        </Sezione>

        {/* 2 · Anagrafica */}
        <Sezione testid="sez-cliente-anagrafica" titolo="Anagrafica"
          azioni={
            <span className="flex gap-2.5 items-center">
              <span data-testid="anagrafica-vuoti" className="text-[13px] text-[#E7D9B2]">
                {tutti ? "anche i campi vuoti" : `${vuotiNascosti} campi vuoti nascosti`}
              </span>
              <button data-testid="anagrafica-mostra-tutti" onClick={() => setTutti(!tutti)} className="btn btn-o btn-piccolo">
                {tutti ? "Nascondi i vuoti" : "Mostra tutti"}
              </button>
            </span>}>
          <ClienteAnagrafica gruppi={s.anagrafica} tutti={tutti} />
        </Sezione>

        {/* 3 · Contabilità */}
        {s.contabilita.visibile && (
          <Sezione testid="sez-cliente-contabilita" titolo="Contabilità"
            azioni={<span className="text-[13px] text-[#E7D9B2]">somma di tutte le pratiche</span>}>
            <ClienteContabilita c={s.contabilita} documenti={s.documenti_emessi} onAzione={vaiAzione} />
          </Sezione>
        )}

        {/* 4 · Documenti */}
        <Sezione testid="sez-cliente-documenti" titolo="Documenti"
          azioni={
            <>
              <span className="text-[13px] text-[#E7D9B2] md:hidden">{(drive?.files || []).length} file</span>
              <span className="hidden md:block">
                <button data-testid="cliente-genera-doc-sez" onClick={() => setGenOpen(true)}
                  className="btn btn-g text-[15px] px-[22px]">Genera documento</button>
              </span>
            </>}>
          <div className="flex flex-col gap-2.5 md:hidden">
            <button data-testid="cliente-genera-doc-mobile" onClick={() => setGenOpen(true)}
              className="btn btn-g btn-larghezza">Genera documento</button>
            <button data-testid="cliente-carica-doc-mobile" onClick={() => setUploadOpen(true)}
              className="btn btn-o btn-larghezza">Carica un file</button>
          </div>
          <ClienteDocumenti cliente={c} files={drive?.files || []} connected={drive?.connected} isLoading={driveLoading} />
        </Sezione>

        {/* 5 · Altro */}
        <Sezione testid="sez-cliente-altro" titolo="Altro" aperta={false}>
          <div className="flex flex-col gap-[18px]">
            <BloccoAltro titolo="Comunicazioni" testid="blocco-comunicazioni">
              <div className="p-3.5">
                <ComunicazioniBox cliente={c} praticaId={(aperte[0] || s.pratiche[0] || {}).id} senzaCornice />
              </div>
            </BloccoAltro>
            {c.referente && (
              <BloccoAltro titolo="Segnalatore" testid="cliente-segnalatore" nota="compare solo se dichiarato">
                <div className="riga">
                  <span className="flex-grow">
                    {nomeRif(c.referente)}
                    {c.referente.telefono ? ` · ${c.referente.telefono}` : ""}
                    {c.referente.email ? ` · ${c.referente.email}` : ""}
                  </span>
                  <button data-testid="cliente-segnalatore-edit" onClick={() => setEditRif(true)}
                    className="text-[13px] font-semibold text-[#8A6E2F]">Modifica</button>
                </div>
              </BloccoAltro>
            )}
            {can("elimina_clienti") && (
              <div className="flex flex-wrap items-center gap-3.5 border-t border-[#E8E6DF] pt-4">
                <span className="text-[13px] text-[#5A5A5A] flex-[999_1_320px]">
                  Si può eliminare solo un cliente senza pratiche. Documenti e cartella Drive restano.
                </span>
                <button data-testid="cliente-elimina" onClick={chiediElimina} className="btn btn-r btn-larghezza">
                  Elimina cliente
                </button>
              </div>
            )}
          </div>
        </Sezione>
      </div>

      {azione && (
        <div className="fixed inset-0 z-[60] bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="scelta-pratica-dialog">
          <div className="bg-white rounded-[6px] w-full max-w-md border-t-2 border-[#A8863F] p-4">
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-[20px] font-bold text-[#1B2A4B]">Su quale pratica?</h3>
              <button data-testid="scelta-pratica-close" onClick={() => setAzione(null)} className="text-[#1B2A4B]"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[13px] text-[#4A4A48] mb-3">
              Incassi, spese e ricevute appartengono a una pratica: scegli quale aprire.
            </p>
            <div className="flex flex-col gap-2">
              {(aperte.length ? aperte : s.pratiche).map((p) => (
                <button key={p.id} data-testid={`scelta-pratica-${p.id}`}
                  onClick={() => navigate(`/pratiche/${p.id}?azione=${azione}`)}
                  className="text-left border border-[#E2E0D9] rounded-[5px] px-3.5 py-2.5 hover:border-[#A8863F] transition-colors">
                  <span className="block text-[14px] font-bold text-[#1B2A4B]">{p.nome}</span>
                  <span className="block text-[12px] text-[#5A5A5A]">
                    {eur(p.valore)} · {p.da_incassare > 0 ? `${eur(p.da_incassare)} da incassare` : "tutto incassato"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {anaOpen && <AnagraficaDialog cliente={c} onClose={() => setAnaOpen(false)}
        onSaved={() => { refresh(); refetchFiles(); }} />}
      {uploadOpen && <UploadDialog cliente={c} onClose={() => setUploadOpen(false)}
        onUploaded={() => { refetchFiles(); refresh(); }} />}
      {genOpen && <DocumentoDialog open={genOpen} onClose={() => setGenOpen(false)} clienteId={c.id}
        clienteNome={`${c.nome} ${c.cognome}`} onGenerated={() => { refetchFiles(); refresh(); }} />}
      {praticaOpen && <NuovaPraticaDialog initialClienteId={c.id} onClose={() => { setPraticaOpen(false); refresh(); }} />}
      {deleteOpen && (
        <ConfirmDialog title="Eliminare il cliente?"
          message={`"${c.nome} ${c.cognome}" verrà eliminato dall'anagrafica.\nLa cartella Drive e i documenti NON vengono eliminati. Se il cliente ha pratiche collegate l'eliminazione viene bloccata.`}
          confirmLabel="Elimina cliente" onConfirm={elimina} onClose={() => setDeleteOpen(false)} />
      )}
    </div>
  );
}
